import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { CcyPairLookupService } from "shared/cubiq-services/lookup/model/impl/CcyPairLookupService";
import { LoggerService } from "@bfx/logger";
import {
  BMRiskUtils,
  RiskAggregate,
} from "shared/cubiq-services/model/benchmarks/helpers/BMRiskUtils";
const logger = LoggerService.for(__dirname);

const MARKET_PTS_GETTER = (allocation: CubiqTS.AllocationDataImpl) =>
  allocation.fillDetails?.marketFillPts;
const MARKET_ALL_IN_GETTER = (allocation: CubiqTS.AllocationDataImpl) =>
  allocation.fillDetails?.marketFillAllInRt;
const CLIENT_SPOT_GETTER = (allocation: CubiqTS.AllocationDataImpl) =>
  allocation.fillDetails?.clientFillSptRt;
const CLIENT_PTS_GETTER = (allocation: CubiqTS.AllocationDataImpl) =>
  allocation.fillDetails?.clientFillPts;
const CLIENT_ALL_IN_GETTER = (allocation: CubiqTS.AllocationDataImpl) =>
  allocation.fillDetails?.clientFillAllInRt;

/** Special constat to denote that the Requested Fwd value cannot be determined precisely */
export const MULTIPLE_FWDS = new CubiqTS.BigNumber(0);

type AllocationsProvider = {
  allocations: CubiqTS.AllocationDataImpl[];
};

/**
 * Represents all allocations for a single pair in a BM order
 */
export class BmarkRiskData implements RiskAggregate {
  public clientIntent: CubiqTS.OrderIntent;

  // Aggregate amounts for the risk - always positive
  public riskAmount: CubiqTS.BigNumber;
  public usdAmount: CubiqTS.BigNumber;
  public counterAmount: CubiqTS.BigNumber;
  public filledBaseAmount: CubiqTS.BigNumber;

  public allocations: CubiqTS.AllocationDataImpl[] = [];

  public pairName: string;
  public baseCcy: string;
  public nonBaseCcy: string;
  public fixedCcy: string;

  // Product flags
  public isSpot: boolean;
  public isSwap: boolean;
  public isMultiAllocation: boolean;
  public isSSP: boolean;

  public isInvert: boolean;
  public isFilled: boolean;
  public isFwdPending: boolean;
  private isComponent: boolean;

  public deskId: CubiqTS.Long;
  public riskId: string;
  public isHedge: boolean = false;

  private minPrecision: number = null;

  private nearLeg: SwapLeg;
  private farLeg: SwapLeg;

  /** Number of allocation Buys */
  public buyCount: number;
  /** Number of Allcation Sells */
  public sellCount: number;

  /** Number of distinct accounts based on accountCode */
  public isSingleAccount: boolean;
  public firstAccountCode: string;
  public firstAccountName: string;

  public useNPV: boolean;
  public npvIntent: CubiqTS.OrderIntent;
  public npvRiskAmount: CubiqTS.BigNumber;
  public npvCounterAmount: CubiqTS.BigNumber;
  public npvFilledBaseAmount: CubiqTS.BigNumber;
  public npvRiskAmountSigned?: CubiqTS.BigNumber;

  public settleTenor: CubiqTS.Tenor;

  constructor(
    public orderData: CubiqTS.OrderData,
    allocations: CubiqTS.AllocationDataImpl[],
  ) {
    if (Array.isArray(allocations)) {
      if (allocations.length > 1) {
        allocations.sort((a, b) => {
          const aVal: number = a?.settleDate?.valueOf() ?? 0;
          const bVal: number = b?.settleDate?.valueOf() ?? 0;
          return aVal - bVal;
        });
      }
      this.allocations = [...allocations];
    }

    this.initialiseOrderLevelValues();
    this.initialiseRiskValues();
  }

  private initialiseOrderLevelValues() {
    const allocation = this.allocations[0];
    this.baseCcy = allocation.baseCcy;
    this.nonBaseCcy = allocation.nonbaseCcy;
    this.deskId = allocation.deskId;
    // Make up a risk ID for roders with no Risk ID e.g. BM ALgos
    this.riskId =
      allocation.riskId?.toString() ??
      (allocation.allocationId != null
        ? allocation.allocationId.toString()
        : allocation.allocationRef);

    let isBMALGO: boolean = CubiqTS.OrderType.BMALGO == this.orderData.orderType;
    let isBMHO: boolean =
      CubiqTS.OrderType.BENCHMARK == this.orderData.orderType &&
      this.orderData.fixingDetails?.bmOrderType == CubiqTS.BenchmarkOrderType.PositionOrder;

    if (isBMALGO || isBMHO) {
      this.riskId = "" + this.orderData.orderId;
      this.isHedge = true;
    }

    this.isSpot = this.orderData.product == CubiqTS.Product.SPT;
    this.isSwap = this.orderData.product == CubiqTS.Product.SWP;
    this.isSSP = this.orderData.product == CubiqTS.Product.SSP;
    // We can also have multi -allocation SPs and FWDs for single tenor
    this.isMultiAllocation = this.allocations.length > 1;
    this.fixedCcy = allocation.fixedCcy;
    this.isInvert = this.orderData.isInvertedOrderFg;
    this.isComponent =
      allocation.allocationType == CubiqTS.AllocationType.COMPONENT ||
      allocation.allocationType == CubiqTS.AllocationType.UNSPAWNEDCOMPONENT;
    this.isFilled = this.orderData.orderState === CubiqTS.OrderState.FILLED;
    this.isFwdPending =
      this.orderData.orderState === CubiqTS.OrderState.PENDING_FWDS ||
      this.orderData.orderState === CubiqTS.OrderState.CANCEL_PENDING_FWDS ||
      this.orderData.orderState === CubiqTS.OrderState.EXPIRY_PENDING_FWDS;

    if (this.isComponent) {
      this.pairName = allocation.baseCcy + "/" + allocation.nonbaseCcy;
      this.settleTenor = allocation.settleTenor;
    } else {
      if (this.orderData.isInvertedOrderFg) {
        this.pairName = allocation.nonbaseCcy + "/" + allocation.baseCcy;
      } else {
        this.pairName = allocation.baseCcy + "/" + allocation.nonbaseCcy;
      }
      this.settleTenor = this.orderData.settleTenor;
    }
  }

  /**
   * agggregate all the allocations and work out net values
   */
  private initialiseRiskValues(): void {
    BMRiskUtils.aggregate(this);

    if (this.isSwap) {
      if (!this.createSwapLegs()) {
        logger.out.warn(`Cannot create swap legs for BM order risk [${this.orderData.orderId}]`);
        this.isSwap = false;
      }
    }

    let firstAccountCode: string;
    let firstAccountName: string;
    let isSingleAccount: boolean = true;
    if (this.allocations.length > 0) {
      const allocation = this.allocations[0];
      firstAccountCode = allocation.accountCode;
      firstAccountName = allocation.accountName;

      if (this.isMultiAllocation) {
        // Check all other allocations to see if they have the same account
        for (let i = 1; i < this.allocations.length; i++) {
          if (this.allocations[i].accountCode !== firstAccountCode) {
            isSingleAccount = false;
            break;
          }
        }
      }
    } else {
      firstAccountCode = this.orderData.accountCode;
      firstAccountName = this.orderData.accountName;
    }
    this.firstAccountCode = firstAccountCode;
    this.firstAccountName = firstAccountName;
    this.isSingleAccount = isSingleAccount;
  }

  /**
   * Foor BM blotter, we need to create aggregates at leg level for SWP orders
   * @returns True if exactly two legs were created and stored on the Risk
   */
  private createSwapLegs(): boolean {
    const legMap = {};
    const legs: SwapLeg[] = [];
    for (const allocation of this.allocations) {
      const key = allocation.settleDate.valueOf();
      let swapLeg: SwapLeg = legMap[key];
      if (!swapLeg) {
        swapLeg = new SwapLeg(allocation);
        legMap[key] = swapLeg;
        legs.push(swapLeg);
      } else {
        swapLeg.addAllocation(allocation);
      }
    }

    if (legs.length == 2) {
      const first = legs[0];
      if (first.valueDate.isBefore(legs[1].valueDate)) {
        this.nearLeg = first;
        this.farLeg = legs[1];
      } else {
        this.nearLeg = legs[1];
        this.farLeg = first;
      }
      this.nearLeg.init();
      this.farLeg.init();
      return true;
    }
    return false;
  }

  /**
   * The minimum precision to use when displaying rates for thid risk. Currently, the
   * min precision is cached. Inverted component rates default to min of spot precision (as was defined for BMark Table)
   * @param ccyPairService
   * @returns The min precision or -1 if no precision found. In case of -1, the max precision for the
   * number shouyld be used
   */
  public getMinPrecision(ccyPairService: CcyPairLookupService): number {
    if (this.minPrecision == null) {
      let ccyPair: CubiqTS.CurrencyPairImpl = ccyPairService.getCcyPair(
        this.baseCcy,
        this.nonBaseCcy,
      );
      if (ccyPair == null) {
        this.minPrecision = -1;
      }
      this.minPrecision =
        this.isInvert && !this.isComponent ? ccyPair.spotInvertDecimalsQty : ccyPair.pipsQty;
    }
    return this.minPrecision;
  }

  public dealRef(): string {
    if (!this.isMultiAllocation) {
      const allocation = this.allocations[0];
      const dealId = allocation.fillDetails?.dealRef;
      return dealId;
    }
    return null;
  }

  /**
   * Get the last fill date for a single allocation BM. The date comes wither form the
   * first allocation or, if that is not populated, the orders lastFillDetails
   * @returns
   */
  public lastFillDate(): CubiqTS.Moment {
    if (this.isFilled) {
      if (!this.isMultiAllocation) {
        if (this.allocations[0].fillDetails) {
          return this.allocations[0].fillDetails.fillDts;
        } else if (this.orderData.lastFillDetails) {
          return this.orderData.lastFillDetails.fillDts;
        }
      }
    }
    return null;
  }

  /**
   * GEt the DealState for the full order. This will return a valid DealState
   * enum iff the order has deals and the DealState for all deals are the same
   * @returns
   */
  public dealState(): CubiqTS.OrderDealState {
    if (this.hasDeal()) {
      const allocation = this.allocations[0];
      const firstState = allocation.fillDetails.dealState;
      if (!this.isMultiAllocation) {
        return firstState;
      } else {
        // Check all other deals to see if they have the same state
        for (let i = 1; i < this.allocations.length; i++) {
          if (this.allocations[i].fillDetails?.dealState !== firstState) {
            return null;
          }
        }
        return firstState;
      }
    }
    return null;
  }

  public allocationRef(): string {
    if (!this.isMultiAllocation) {
      const allocation = this.allocations[0];
      const allocationId = allocation.allocationRef;
      return allocationId;
    }
    return null;
  }

  public hasDeal(): boolean {
    const allocation = this.allocations[0];
    const dealId = allocation.fillDetails?.dealRef;
    return dealId != null;
  }

  public get orderRef(): string {
    return this.orderData.orderRef;
  }

  public get nearIntent(): CubiqTS.OrderIntent {
    if (this.isSwap) {
      return this.nearLeg.clientIntent;
    }
    return this.orderData.clientIntent;
  }

  public get nearAmount(): CubiqTS.BigNumber {
    if (this.isSwap) {
      return this.nearLeg.riskAmount;
    }
    return this.riskAmount;
  }

  public get nearNPVAmount(): CubiqTS.BigNumber {
    if (this.isSwap) {
      return this.nearLeg.npvRiskAmountSigned;
    }
    return this.npvRiskAmountSigned;
  }

  public get nearUSDAmount(): CubiqTS.BigNumber {
    if (this.isSwap) {
      return this.nearLeg.usdAmount;
    }
    return this.usdAmount;
  }

  public get farIntent(): CubiqTS.OrderIntent {
    if (this.isSwap) {
      return this.farLeg.clientIntent;
    }
    return this.orderData.clientIntent;
  }

  public get farAmount(): CubiqTS.BigNumber {
    if (this.isSwap) {
      return this.farLeg.riskAmount;
    }
    return this.riskAmount;
  }

  public get farNPVAmount(): CubiqTS.BigNumber {
    if (this.isSwap) {
      return this.farLeg.npvRiskAmountSigned;
    }
    return this.npvRiskAmountSigned;
  }

  public get farUSDAmount(): CubiqTS.BigNumber {
    if (this.isSwap) {
      return this.farLeg.usdAmount;
    }
    return this.usdAmount;
  }

  public get netIntent(): CubiqTS.OrderIntent {
    return this.clientIntent;
  }

  public get nearTenor() {
    if (this.isSwap) {
      return this.nearLeg.tenor;
    }
    return null;
  }
  public get farTenor() {
    if (this.isSwap) {
      return this.farLeg.tenor;
    }
    return null;
  }
  public get nearValueDate() {
    if (this.isSwap) {
      return this.nearLeg.valueDate;
    }
    return null;
  }
  public get farValueDate() {
    if (this.isSwap) {
      return this.farLeg.valueDate;
    }
    return null;
  }

  /**
   * Returns value to be used as settle date - only return if all allocations
   * have the same date
   */
  public get valueDate() {
    if (this.isSwap) {
      return this.nearLeg.valueDate;
    }
    const firstDate = this.allocations[0].settleDate;
    return firstDate;
  }

  /**
   * Get the Market pts to use - for Swaps this will always return the first allocation
   * for use in sorting
   * @returns
   */
  public getMarketPts(): CubiqTS.BigNumber {
    if (this.isSpot) {
      return null;
    }
    if (this.isFilled) {
      if (this.isSwap) {
        const allocation = this.nearLeg.allocation;
        return this.getAllocationMarketPts(allocation);
      } else if (!this.isMultiAllocation || hasSameAllocationValue(this, MARKET_PTS_GETTER)) {
        const allocation = this.allocations[0];
        return this.getAllocationMarketPts(allocation);
      } else {
        return MULTIPLE_FWDS;
      }
    }
    return null;
  }

  /**
   * For Swaps return the common market points for near leg, or MULTIPLE_FWDS
   * if different allocations have different pts.
   *
   * @returns null if the order is not a swap or not filled
   */
  public getNearMarketPts(): CubiqTS.BigNumber {
    if (this.isFilled && this.isSwap) {
      if (hasSameAllocationValue(this.nearLeg, MARKET_PTS_GETTER)) {
        return this.getAllocationMarketPts(this.nearLeg.allocation);
      }
      return MULTIPLE_FWDS;
    }
    return null;
  }

  /**
   * For Swaps return the common market points for far leg, or MULTIPLE_FWDS
   * if different allocations have different pts.
   *
   * @returns null if the order is not a swap or not filled
   */
  public getFarMarketPts(): CubiqTS.BigNumber {
    if (this.isFilled && this.isSwap) {
      if (hasSameAllocationValue(this.farLeg, MARKET_PTS_GETTER)) {
        return this.getAllocationMarketPts(this.farLeg.allocation);
      }
      return MULTIPLE_FWDS;
    }
    return null;
  }

  private getAllocationMarketPts(allocation: CubiqTS.AllocationData): CubiqTS.BigNumber {
    if (this.isFilled && allocation.fillDetails) {
      return allocation.fillDetails.marketFillPts;
    }
    return null;
  }

  /**
   * Get the Market All-in to use - for Swaps this will always return the first allocation
   * for use in sorting
   * @returns
   */
  public getMarketAllIn(): CubiqTS.BigNumber {
    if (this.isFilled) {
      if (this.isSwap) {
        const allocation = this.nearLeg.allocation;
        return this.getAllocationMarketAllIn(allocation);
      } else if (!this.isMultiAllocation || hasSameAllocationValue(this, MARKET_ALL_IN_GETTER)) {
        const allocation = this.allocations[0];
        return this.getAllocationMarketAllIn(allocation);
      } else {
        return MULTIPLE_FWDS;
      }
    }
    return null;
  }

  /**
   * For Swaps return the common market all-in for near leg, or MULTIPLE_FWDS
   * if different allocations have different values.
   *
   * @returns null if the order is not a swap or not filled
   */
  public getNearMarketAllIn(): CubiqTS.BigNumber {
    if (this.isFilled && this.isSwap) {
      if (hasSameAllocationValue(this.nearLeg, MARKET_ALL_IN_GETTER)) {
        return this.getAllocationMarketAllIn(this.nearLeg.allocation);
      }
      return MULTIPLE_FWDS;
    }
    return null;
  }

  /**
   * For Swaps return the common market all-in for far leg, or MULTIPLE_FWDS
   * if different allocations have different values.
   *
   * @returns null if the order is not a swap or not filled
   */
  public getFarMarketAllIn(): CubiqTS.BigNumber {
    if (this.isFilled && this.isSwap) {
      if (hasSameAllocationValue(this.farLeg, MARKET_ALL_IN_GETTER)) {
        return this.getAllocationMarketAllIn(this.farLeg.allocation);
      }
      return MULTIPLE_FWDS;
    }
    return null;
  }

  private getAllocationMarketAllIn(allocation: CubiqTS.AllocationData): CubiqTS.BigNumber {
    if (this.isFilled && allocation.fillDetails) {
      return allocation.fillDetails.marketFillAllInRt;
    }
    return null;
  }

  /**
   * Get the Client spot to use - for Swaps this will always return the first allocation
   * for use in sorting
   * @returns
   */
  public getClientSpot(): CubiqTS.BigNumber {
    if (this.isFilled || this.isFwdPending) {
      if (!this.isMultiAllocation || hasSameAllocationValue(this, CLIENT_SPOT_GETTER)) {
        const allocation = this.allocations[0];
        return this.getAllocationClientSpot(allocation);
      } else {
        return MULTIPLE_FWDS;
      }
    }
    return null;
  }

  private getAllocationClientSpot(allocation: CubiqTS.AllocationData): CubiqTS.BigNumber {
    if ((this.isFilled || this.isFwdPending) && allocation.fillDetails) {
      return allocation.fillDetails.clientFillSptRt;
    }
    return null;
  }

  /**
   * Get the Client pts to use - for Swaps this will always return the first allocation
   * for use in sorting
   * @returns
   */
  public getClientPts(): CubiqTS.BigNumber {
    if (this.isSpot) {
      return null;
    }
    if (this.isFilled) {
      if (this.isSwap) {
        const allocation = this.nearLeg.allocation;
        return this.getAllocationClientPts(allocation);
      } else if (!this.isMultiAllocation || hasSameAllocationValue(this, CLIENT_PTS_GETTER)) {
        const allocation = this.allocations[0];
        return this.getAllocationClientPts(allocation);
      } else {
        return MULTIPLE_FWDS;
      }
    }
    return null;
  }

  /**
   * For Swaps return the common client Pts for near leg, or MULTIPLE_FWDS
   * if different allocations have different values.
   *
   * @returns null if the order is not a swap or not filled
   */
  public getNearClientPts(): CubiqTS.BigNumber {
    if (this.isFilled && this.isSwap) {
      if (hasSameAllocationValue(this.nearLeg, CLIENT_PTS_GETTER)) {
        return this.getAllocationClientPts(this.nearLeg.allocation);
      }
      return MULTIPLE_FWDS;
    }
    return null;
  }

  /**
   * For Swaps return the common client Pts for far leg, or MULTIPLE_FWDS
   * if different allocations have different values.
   *
   * @returns null if the order is not a swap or not filled
   */
  public getFarClientPts(): CubiqTS.BigNumber {
    if (this.isFilled && this.isSwap) {
      if (hasSameAllocationValue(this.farLeg, CLIENT_PTS_GETTER)) {
        return this.getAllocationClientPts(this.farLeg.allocation);
      }
      return MULTIPLE_FWDS;
    }
    return null;
  }

  private getAllocationClientPts(allocation: CubiqTS.AllocationData): CubiqTS.BigNumber {
    if (this.isFilled && allocation.fillDetails) {
      return allocation.fillDetails.clientFillPts;
    }
    return null;
  }

  /**
   * Get the Client all-in to use - for Swaps this will always return the first allocation
   * for use in sorting
   * @returns
   */
  public getClientAllIn(): CubiqTS.BigNumber {
    if (this.isFilled) {
      if (this.isSwap) {
        const allocation = this.nearLeg.allocation;
        return this.getAllocationClientAllIn(allocation);
      } else if (!this.isMultiAllocation || hasSameAllocationValue(this, CLIENT_ALL_IN_GETTER)) {
        const allocation = this.allocations[0];
        return this.getAllocationClientAllIn(allocation);
      } else {
        return MULTIPLE_FWDS;
      }
    }
    return null;
  }

  /**
   * For Swaps return the common client all-in for near leg, or MULTIPLE_FWDS
   * if different allocations have different values.
   *
   * @returns null if the order is not a swap or not filled
   */
  public getNearClientAllIn(): CubiqTS.BigNumber {
    if (this.isFilled && this.isSwap) {
      if (hasSameAllocationValue(this.nearLeg, CLIENT_ALL_IN_GETTER)) {
        return this.getAllocationClientAllIn(this.nearLeg.allocation);
      }
      return MULTIPLE_FWDS;
    }
    return null;
  }

  /**
   * For Swaps return the common client all-in for far leg, or MULTIPLE_FWDS
   * if different allocations have different values.
   *
   * @returns null if the order is not a swap or not filled
   */
  public getFarClientAllIn(): CubiqTS.BigNumber {
    if (this.isFilled && this.isSwap) {
      if (hasSameAllocationValue(this.farLeg, CLIENT_ALL_IN_GETTER)) {
        return this.getAllocationClientAllIn(this.farLeg.allocation);
      }
      return MULTIPLE_FWDS;
    }
    return null;
  }

  private getAllocationClientAllIn(allocation: CubiqTS.AllocationData): CubiqTS.BigNumber {
    if (this.isFilled && allocation.fillDetails) {
      return allocation.fillDetails.clientFillAllInRt;
    }
    return null;
  }

  /**
   * The Market swap pts if both near points and far points resolve to single values. If either
   * leg has mutiple values then Multiple is returned
   * @returns null if order is not filled or not a swap
   */
  public getMarketSwapPts(): CubiqTS.BigNumber {
    if (this.isFilled && this.isSwap) {
      const nearPts = this.getNearMarketPts();
      const farPts = this.getFarMarketPts();
      if (nearPts === MULTIPLE_FWDS || farPts === MULTIPLE_FWDS) {
        return MULTIPLE_FWDS;
      }

      if (nearPts && farPts) {
        const swapPts = farPts.minus(nearPts);
        return swapPts;
      }
    }
    return null;
  }

  /**
   * The Client swap pts if both near points and far points resolve to single values If either
   * leg has mutiple values then Multiple is returned
   * @returns null if order is not filled or not a swap
   */
  public getClientSwapPts(): CubiqTS.BigNumber {
    if (this.isFilled && this.isSwap) {
      const nearPts = this.getNearClientPts();
      const farPts = this.getFarClientPts();
      if (nearPts === MULTIPLE_FWDS || farPts === MULTIPLE_FWDS) {
        return MULTIPLE_FWDS;
      }
      if (nearPts && farPts) {
        const swapPts = farPts.minus(nearPts);
        return swapPts;
      }
    }
    return null;
  }
}

/**
 * Aggregates all allocations for a single Swap leg
 */
class SwapLeg {
  public allocations: CubiqTS.AllocationDataImpl[];
  public tenor: CubiqTS.Tenor;
  public valueDate: CubiqTS.Moment;

  public clientIntent: CubiqTS.OrderIntent;

  public riskAmount: CubiqTS.BigNumber;
  public usdAmount: CubiqTS.BigNumber;
  public counterAmount: CubiqTS.BigNumber;
  public filledBaseAmount: CubiqTS.BigNumber;

  public buyCount: number;
  public sellCount: number;

  public fixedCcy: string;

  public useNPV: boolean;
  public npvIntent: CubiqTS.OrderIntent;
  public npvRiskAmount: CubiqTS.BigNumber;
  public npvCounterAmount: CubiqTS.BigNumber;
  public npvFilledBaseAmount: CubiqTS.BigNumber;
  public npvRiskAmountSigned: CubiqTS.BigNumber;

  constructor(allocation: CubiqTS.AllocationDataImpl) {
    this.tenor = allocation.settleTenor;
    this.valueDate = allocation.settleDate;
    this.fixedCcy = allocation.fixedCcy;
    this.allocations = [allocation];
  }

  public addAllocation(allocation) {
    this.allocations.push(allocation);
  }

  public init(): void {
    BMRiskUtils.aggregate(this);
  }

  public get allocation(): CubiqTS.AllocationDataImpl {
    return this.allocations[0];
  }
}

/**
 * Do all allocations for this leg have the save value for the getter argument?
 * @param getter
 * @returns
 */
function hasSameAllocationValue(
  provider: AllocationsProvider,
  getter: (allocation: CubiqTS.AllocationDataImpl) => CubiqTS.BigNumber,
): boolean {
  if (provider.allocations.length > 1) {
    const prime: CubiqTS.BigNumber = getter(provider.allocations[0]) ?? new CubiqTS.BigNumber(0);
    for (let i = 1; i < provider.allocations.length; i++) {
      const value: CubiqTS.BigNumber = getter(provider.allocations[i]);
      if (value == null || !prime.isEqualTo(value)) {
        return false;
      }
    }
  }
  return true;
}
